import CreateEditOrganizationModal from '@components/organisms/organization/CreateEditOrganizationModal';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { Modal } from '@components/molecules/Modal';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import UserInfoModal from '@components/organisms/user/UserInfoModal';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { EditIcon24 } from '@components/ui/Icons';
import { Button } from '@components/atoms/Button';
import { Badge } from '@components/atoms/Badge';
import { Input } from '@components/atoms/Input';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { useTranslation } from '@hooks/useTranslation';
import { Organization, OrganizationStatus, CompletenessGateMode } from '@models/organization.model';
import { OrganizationSubscriptionStatus } from '@models/subscription';
import SubscriptionStatusBadge from '@components/organisms/organization/SubscriptionStatusBadge';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import { useState } from 'react';

// TODO: With some work this could become generic if we replace documents for a generic type

/** Subscription statuses a super admin can extend into a bounded free period. */
const EXTENDABLE_STATUSES: string[] = [
  OrganizationSubscriptionStatus.TRIAL,
  OrganizationSubscriptionStatus.TRIAL_EXPIRED,
  OrganizationSubscriptionStatus.ACTIVE,
];

export const canExtendFreePeriod = (org: Organization): boolean =>
  !org.hasNmiSubscription &&
  !!org.subscriptionStatus &&
  EXTENDABLE_STATUSES.includes(org.subscriptionStatus);

// Owners come back as two comma-joined strings (names + emails) in matching order.
// Pair them up so we can render each owner as "Name <email>" in a single column.
const zipOwners = (names?: string, emails?: string) => {
  const n = (names || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const e = (emails || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const len = Math.max(n.length, e.length);
  return Array.from({ length: len }, (_, i) => ({
    name: n[i] || '',
    email: e[i] || '',
  }));
};

interface OrganizationsListProps {
  // deleteDocument
  handleDisableOrganization: (organizationId: string) => void;
  handleEnableOrganization: (organizationId: string) => void;
  handleToggleInvoicingPaused: (organizationId: string, paused: boolean) => void;
  handleBulkToggleInvoicingPaused: (ids: string[], paused: boolean) => void;
  handleBulkToggleStatus: (ids: string[], enable: boolean) => void;
  handleEditOrganization: (
    name: string,
    organizationId: string,
    showAsSubcontractor: boolean,
    atavEnabled: boolean,
    completenessGateMode: CompletenessGateMode,
    onSuccess?: () => void,
  ) => void;
  updateAtavEnabled: (
    name: string,
    atavEnabled: boolean,
    organizationId?: string,
  ) => void;
  updateShowAsSubcontractor: (
    name: string,
    showAsSubcontractor: boolean,
    organizationId: string,
  ) => void;
  handleAssignPlan: (organization: Organization) => void;
  handleExtendTrial: (organization: Organization) => void;
  // common
  count: number;
  organizations: Organization[];
  // search
  search: string;
  handleSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  // filter
  filter: string;
  handleFilterChange: (filter: string) => void;
  // order
  orderDirection: 'asc' | 'desc';
  orderBy: string | null;
  handleRequestSort: (property: string) => void;
  // pagination
  page: number;
  handleChangePage: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => void;
  rowsPerPage: number;
  handleChangeRowsPerPage: (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => void;
}

const OrganizationsList: React.FC<OrganizationsListProps> = ({
  count,
  organizations,
  search,
  handleSearchChange,
  filter,
  handleFilterChange,
  orderBy,
  orderDirection,
  handleRequestSort,
  page,
  handleChangePage,
  rowsPerPage,
  handleChangeRowsPerPage,
  handleEnableOrganization,
  handleDisableOrganization,
  handleToggleInvoicingPaused,
  handleBulkToggleInvoicingPaused,
  handleBulkToggleStatus,
  handleEditOrganization,
  updateShowAsSubcontractor,
  updateAtavEnabled,
  handleAssignPlan,
  handleExtendTrial,
}) => {
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] =
    useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showConfirmEnableModal, setShowConfirmEnableModal] =
    useState<boolean>(false);
  const [showUserInfoModal, setShowUserInfoModal] = useState<boolean>(false);
  const [showInvoicingPausedModal, setShowInvoicingPausedModal] =
    useState<boolean>(false);
  const [organizationToToggleInvoicing, setOrganizationToToggleInvoicing] =
    useState<Organization | undefined>(undefined);
  // Bulk invoicing pause/resume across multiple selected organizations.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkInvoicingModal, setShowBulkInvoicingModal] =
    useState<boolean>(false);
  const [bulkPause, setBulkPause] = useState<boolean>(true);
  const [showBulkStatusModal, setShowBulkStatusModal] =
    useState<boolean>(false);
  const [bulkEnable, setBulkEnable] = useState<boolean>(false);
  // TODO: With some work this could become generic
  const [organizationToDelete, setOrganizationToDelete] = useState<
    Organization | undefined
  >(undefined);
  const [organizationToEdit, setOrganizationToEdit] = useState<
    Organization | undefined
  >(undefined);
  const [organizationToEnable, setOrganizationToEnable] = useState<
    Organization | undefined
  >(undefined);
  const [userToDetail, setUserToDetail] = useState<any | undefined>(undefined);

  const localeCommon = useTranslation('common');
  const t = useTranslation('organizations');
  const tEnumLabels = useTranslation('enumLabels');
  // Client-side filter over the loaded page: '' means every subscription status.
  const [subscriptionFilter, setSubscriptionFilter] = useState<string>('');

  const toggleConfirmDeleteModal = (organization?: Organization) => {
    setShowConfirmDeleteModal(true);
    setOrganizationToDelete(organization);
  };

  const toggleEnableConfirmModal = (organization?: Organization) => {
    setOrganizationToEnable(organization);
    setShowConfirmEnableModal(true);
  };

  const disableOrganization = () => {
    if (showConfirmDeleteModal) {
      handleDisableOrganization(organizationToDelete?.organizationId || '');
    }
    setShowConfirmDeleteModal(false);
  };

  const visibleOrganizations = subscriptionFilter
    ? organizations.filter((o) => o.subscriptionStatus === subscriptionFilter)
    : organizations;

  const pageIds = visibleOrganizations.map((o) => o.organizationId);
  const allPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));

  const toggleSelectAllPage = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      pageIds.forEach((id) => (checked ? next.add(id) : next.delete(id)));
      return next;
    });
  };

  const toggleRowSelected = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const openBulkInvoicingModal = (pause: boolean) => {
    setBulkPause(pause);
    setShowBulkInvoicingModal(true);
  };

  const confirmBulkInvoicing = () => {
    handleBulkToggleInvoicingPaused(Array.from(selectedIds), bulkPause);
    setShowBulkInvoicingModal(false);
    clearSelection();
  };

  const openBulkStatusModal = (enable: boolean) => {
    setBulkEnable(enable);
    setShowBulkStatusModal(true);
  };

  const confirmBulkStatus = () => {
    handleBulkToggleStatus(Array.from(selectedIds), bulkEnable);
    setShowBulkStatusModal(false);
    clearSelection();
  };

  const toggleInvoicingPausedModal = (organization?: Organization) => {
    setOrganizationToToggleInvoicing(organization);
    setShowInvoicingPausedModal(true);
  };

  const confirmToggleInvoicingPaused = () => {
    if (organizationToToggleInvoicing) {
      handleToggleInvoicingPaused(
        organizationToToggleInvoicing.organizationId,
        !organizationToToggleInvoicing.invoicingPaused,
      );
    }
    setShowInvoicingPausedModal(false);
    setOrganizationToToggleInvoicing(undefined);
  };

  const toggleEditModal = (organization: Organization) => {
    setOrganizationToEdit(organization);
    setShowEditModal(true);
  };

  const enableOrganization = () => {
    if (showConfirmEnableModal) {
      handleEnableOrganization(organizationToEnable?.organizationId || '');
    }
    setShowConfirmEnableModal(false);
    setOrganizationToEnable(undefined);
  };

  const modalClose = () => {
    setShowConfirmDeleteModal(false);
    setShowEditModal(false);
    setShowConfirmEnableModal(false);
    setShowUserInfoModal(false);
    setShowInvoicingPausedModal(false);
    setUserToDetail(undefined);
    setOrganizationToDelete(undefined);
    setOrganizationToEdit(undefined);
    setOrganizationToToggleInvoicing(undefined);
  };

  const showUserInfo = (email: string, organization: string) => {
    setShowUserInfoModal(true);
    setUserToDetail({ email, organization });
  };

  const sortedTableHeaderCell = ({
    label,
    property,
    align,
  }: {
    label: string;
    property: string;
    align?: 'right' | 'left' | 'center' | 'inherit' | 'justify' | undefined;
  }) => {
    const alignClass =
      align === 'center'
        ? 'text-center'
        : align === 'right'
          ? 'text-right'
          : 'text-left';

    const sortIcon =
      orderBy === property ? (orderDirection === 'asc' ? ' ↑' : ' ↓') : '';

    return (
      <th
        className={`px-4 py-3 ${alignClass} text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 dark:hover:text-white`}
        onClick={() => handleRequestSort(property)}>
        {label}
        {sortIcon}
      </th>
    );
  };

  const activeFilterItems: SelectOption[] = [
    { value: 'ENABLED', label: t.stateEnabled },
    { value: 'SUSPENDED', label: t.stateSuspended },
    { value: 'DISABLED', label: t.stateDisabled },
  ];

  const subscriptionFilterItems: SelectOption[] = [
    { value: '', label: t.subscriptionFilterAll },
    {
      value: OrganizationSubscriptionStatus.TRIAL,
      label: tEnumLabels.subscriptionStatusTrial,
    },
    {
      value: OrganizationSubscriptionStatus.TRIAL_EXPIRED,
      label: tEnumLabels.subscriptionStatusTrialExpired,
    },
    {
      value: OrganizationSubscriptionStatus.ACTIVE,
      label: tEnumLabels.subscriptionStatusActive,
    },
    {
      value: OrganizationSubscriptionStatus.PAST_DUE,
      label: tEnumLabels.subscriptionStatusPastDue,
    },
    {
      value: OrganizationSubscriptionStatus.CANCELED,
      label: tEnumLabels.subscriptionStatusCanceled,
    },
    {
      value: OrganizationSubscriptionStatus.PAUSED,
      label: tEnumLabels.subscriptionStatusPaused,
    },
  ];

  const renderBilling = (row: Organization) => {
    if (row.hasNmiSubscription) {
      return (
        <span
          title={t.billingNmiTooltip}
          className="inline-flex items-center gap-1.5 text-gray-900 dark:text-white">
          <span className="h-2 w-2 rounded-full bg-green-500" aria-hidden="true" />
          {t.billingNmi}
        </span>
      );
    }
    if (row.subscriptionStatus === OrganizationSubscriptionStatus.ACTIVE) {
      return (
        <span
          title={t.billingFreeAdminTooltip}
          className="text-amber-600 dark:text-amber-400">
          {t.billingFreeAdmin}
        </span>
      );
    }
    if (row.hasPaymentMethod) {
      return (
        <span
          title={t.billingCardNotStartedTooltip}
          className="text-gray-500 dark:text-gray-400">
          {t.billingCardNotStarted}
        </span>
      );
    }
    return (
      <span
        title={t.billingNoCardTooltip}
        className="text-gray-400 italic">
        {t.billingNoCard}
      </span>
    );
  };

  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    row: (typeof organizations)[0],
  ) => {
    const isChecked = event.target.checked;
    updateShowAsSubcontractor(row.name, isChecked, row.organizationId);
  };

  const handleAtavCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    row: (typeof organizations)[0],
  ) => {
    updateAtavEnabled(row.name, event.target.checked, row.organizationId);
  };

  const handleEditSubmit = (
    name: string,
    organizationId: string,
    showAsSubcontractor: boolean,
    atavEnabled: boolean,
    completenessGateMode: CompletenessGateMode,
  ) => {
    handleEditOrganization(
      name,
      organizationId,
      showAsSubcontractor,
      atavEnabled,
      completenessGateMode,
      () => {
        modalClose();
      },
    );
  };

  return (
    <div className="w-full">
      <div className="flex items-center mb-4 gap-4">
        <div className="min-w-[250px]">
          <Input
            type="text"
            value={search}
            onChange={handleSearchChange}
            placeholder={t.searchPlaceholder}
            className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>
        <div className="min-w-[150px]">
          <ReactSelect
            value={
              activeFilterItems.find((item) => item.value === filter) || null
            }
            onChange={(value) => {
              const selectedValue = value as SelectOption | null;
              handleFilterChange(selectedValue?.value?.toString() || '');
            }}
            options={activeFilterItems}
            className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            isClearable={false}
            isSearchable={false}
          />
        </div>
        <div className="min-w-[200px]">
          <ReactSelect
            aria-label={t.subscriptionFilterLabel}
            value={
              subscriptionFilterItems.find(
                (item) => item.value === subscriptionFilter,
              ) || null
            }
            onChange={(value) => {
              const selectedValue = value as SelectOption | null;
              setSubscriptionFilter(selectedValue?.value?.toString() || '');
            }}
            options={subscriptionFilterItems}
            className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            isClearable={false}
            isSearchable={false}
          />
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4 px-4 py-3 rounded-xl bg-indigo-50 dark:bg-gray-700 border border-indigo-100 dark:border-gray-600 shadow-sm">
          <div className="flex items-center gap-2 text-sm font-semibold text-indigo-900 dark:text-indigo-200">
            <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full bg-indigo-600 text-white text-xs">
              {selectedIds.size}
            </span>
            selected
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="tertiary"
              leftIcon={<PauseCircleOutlineIcon sx={{ fontSize: 18 }} />}
              onClick={() => openBulkInvoicingModal(true)}>
              {t.pauseInvoicing}
            </Button>
            <Button
              variant="tertiary"
              leftIcon={<PlayCircleOutlineIcon sx={{ fontSize: 18 }} />}
              onClick={() => openBulkInvoicingModal(false)}>
              {t.resumeInvoicing}
            </Button>
            <span className="w-px h-6 bg-indigo-200 dark:bg-gray-500" />
            <Button
              variant="tertiary"
              leftIcon={<PersonOffIcon sx={{ fontSize: 18 }} />}
              onClick={() => openBulkStatusModal(false)}>
              {localeCommon['disable']}
            </Button>
            <Button
              variant="tertiary"
              leftIcon={<PersonAddAlt1Icon sx={{ fontSize: 18 }} />}
              onClick={() => openBulkStatusModal(true)}>
              {localeCommon['enable']}
            </Button>
            <span className="w-px h-6 bg-indigo-200 dark:bg-gray-500" />
            <Button variant="tertiary" onClick={clearSelection}>
              {t.clearSelection}
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
        <div className="max-h-[560px] overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700/90 backdrop-blur">
              <tr className="border-b border-gray-200 dark:border-gray-600">
                <th className="px-4 py-3 text-center w-12">
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    onChange={(e) => toggleSelectAllPage(e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    aria-label={t.selectAllOnPage}
                  />
                </th>
                {sortedTableHeaderCell({ label: localeCommon['name'], property: 'name' })}
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t.owners}
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t.subcontractor}
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t.atav}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t.plan}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[200px]">
                  {t.subscriptionColumn}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t.billingColumn}
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t.state}
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {localeCommon['actions']}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {visibleOrganizations.map((row, idx) => {
                const isSelected = selectedIds.has(row.organizationId);
                const isDisabled = row.status === OrganizationStatus.DISABLED;
                return (
                  <tr
                    key={`${idx}-${row.name}`}
                    className={`transition-colors ${
                      isSelected
                        ? 'bg-indigo-50/70 dark:bg-gray-700/50'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
                    }`}>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) =>
                          toggleRowSelected(row.organizationId, e.target.checked)
                        }
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        aria-label={`Select ${row.name}`}
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-700 text-sm font-semibold uppercase dark:bg-indigo-900/40 dark:text-indigo-200">
                          {row.name?.charAt(0) || '?'}
                        </span>
                        <span
                          title={row.name}
                          className="font-medium text-gray-900 dark:text-white max-w-[180px] truncate">
                          {row.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm align-top">
                      <div className="flex flex-col gap-1 max-w-[240px]">
                        {zipOwners(row.ownersNames, row.ownersEmails).map(
                          (owner, i) => (
                            <div key={i} className="min-w-0">
                              {owner.name && (
                                <p className="truncate font-medium text-gray-900 dark:text-white">
                                  {owner.name}
                                </p>
                              )}
                              {owner.email && (
                                <button
                                  type="button"
                                  title={owner.email}
                                  onClick={() =>
                                    showUserInfo(owner.email, row.organizationId)
                                  }
                                  className="block max-w-full truncate text-teal-600 hover:underline">
                                  {owner.email}
                                </button>
                              )}
                            </div>
                          ),
                        )}
                        {zipOwners(row.ownersNames, row.ownersEmails).length ===
                          0 && (
                          <span className="text-gray-400 italic">{t.noOwners}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <input
                        type="checkbox"
                        checked={row.showAsSubcontractor}
                        onChange={(event) => handleCheckboxChange(event, row)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        aria-label={t.showAsSubcontractor}
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <input
                        type="checkbox"
                        checked={row.atavEnabled ?? false}
                        onChange={(event) => handleAtavCheckboxChange(event, row)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                        aria-label={t.atavEnabled}
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <span
                          className={
                            row.planName
                              ? 'text-gray-900 dark:text-white'
                              : 'text-gray-400 italic'
                          }>
                          {row.planName || 'No plan'}
                        </span>
                        <Button
                          variant="tertiary"
                          className="flex items-center justify-center p-1"
                          onClick={() => handleAssignPlan(row)}
                          title={t.assignPlan}>
                          <EditIcon24 />
                        </Button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <SubscriptionStatusBadge
                        status={row.subscriptionStatus}
                        trialEndsAt={row.trialEndsAt}
                        trialExpiredAt={row.trialExpiredAt}
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {renderBilling(row)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex flex-col items-center gap-1">
                        <Badge
                          size="sm"
                          variant={
                            isDisabled
                              ? 'secondary'
                              : row.status === OrganizationStatus.SUSPENDED
                                ? 'warning'
                                : 'success'
                          }>
                          {isDisabled
                            ? t.stateDisabled
                            : row.status === OrganizationStatus.SUSPENDED
                              ? t.stateSuspended
                              : t.stateEnabled}
                        </Badge>
                        {row.invoicingPaused && (
                          <Badge size="sm" variant="warning">
                            {t.invoicingPaused}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <div className="flex justify-end gap-1">
                        {!isDisabled && (
                          <>
                            <Button
                              variant="tertiary"
                              className="flex items-center justify-center"
                              onClick={() => toggleEditModal(row)}
                              title={t.editOrganization}>
                              <EditIcon24 />
                            </Button>
                            <Button
                              variant="tertiary"
                              className="flex items-center justify-center"
                              onClick={() => toggleInvoicingPausedModal(row)}
                              title={
                                row.invoicingPaused
                                  ? t.resumeInvoicing
                                  : t.pauseInvoicing
                              }>
                              {row.invoicingPaused ? (
                                <PlayCircleOutlineIcon className="text-amber-600" />
                              ) : (
                                <PauseCircleOutlineIcon />
                              )}
                            </Button>
                          </>
                        )}
                        {canExtendFreePeriod(row) && (
                          <Button
                            variant="tertiary"
                            className="flex items-center justify-center"
                            onClick={() => handleExtendTrial(row)}
                            title={t.extendFreePeriod}
                            aria-label={t.extendFreePeriod}>
                            <HourglassBottomIcon />
                          </Button>
                        )}
                        {!isDisabled && (
                          <Button
                            variant="tertiary"
                            className="flex items-center justify-center"
                            onClick={() => toggleConfirmDeleteModal(row)}
                            title={t.disableOrganizationAction}>
                            <PersonOffIcon />
                          </Button>
                        )}
                        {isDisabled && (
                          <Button
                            variant="tertiary"
                            className="flex items-center justify-center"
                            onClick={() => toggleEnableConfirmModal(row)}
                            title={t.enableOrganizationAction}>
                            <PersonAddAlt1Icon />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {visibleOrganizations.length === 0 && (
                <tr>
                  <td
                    colSpan={10}
                    className="px-4 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                    {subscriptionFilter
                      ? t.noOrganizationsMatchOnPage
                      : t.noOrganizationsMatch}
                    {subscriptionFilter && (
                      <div className="mt-2">
                        <Button
                          variant="tertiary"
                          onClick={() => setSubscriptionFilter('')}>
                          {t.clearFilters}
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-end px-4 py-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t.pageRange
                .replace(
                  '{range}',
                  count === 0
                    ? '0'
                    : `${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, count)}`,
                )
                .replace('{count}', String(count))}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="tertiary"
                size="md"
                onClick={(e) => handleChangePage(e, page - 1)}
                disabled={page === 0}>
                {localeCommon['previous']}
              </Button>
              <Button
                variant="tertiary"
                size="md"
                onClick={(e) => handleChangePage(e, page + 1)}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}>
                {localeCommon['next']}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        onConfirm={disableOrganization}
        isOpen={showConfirmDeleteModal}
        onClose={modalClose}
        title={t.disableOrganization}
        description={`Are you sure you want to disable the organization ${organizationToDelete?.name}? This action will prevent the organization from being used in new projects.`}
        confirmText={localeCommon['disable']}
        variant="danger"
      />

      <ConfirmationModal
        onConfirm={enableOrganization}
        isOpen={showConfirmEnableModal}
        onClose={modalClose}
        title={t.enableOrganization}
        description={`Are you sure you want to enable the organization ${organizationToEnable?.name}? This will allow the organization to be used in projects again.`}
        confirmText={localeCommon['enable']}
        variant="success"
      />

      <ConfirmationModal
        onConfirm={confirmToggleInvoicingPaused}
        isOpen={showInvoicingPausedModal}
        onClose={modalClose}
        title={
          organizationToToggleInvoicing?.invoicingPaused
            ? t.resumeInvoicingTitle
            : t.pauseInvoicingTitle
        }
        description={
          organizationToToggleInvoicing?.invoicingPaused
            ? `Are you sure you want to resume invoicing for ${organizationToToggleInvoicing?.name}? Users will regain access to the invoicing page.`
            : `Are you sure you want to pause invoicing for ${organizationToToggleInvoicing?.name}? Its users will see a "work in progress" message on the invoicing page and won't be able to create or send invoices until you resume it.`
        }
        confirmText={
          organizationToToggleInvoicing?.invoicingPaused ? t.resume : t.pause
        }
        variant="warning"
      />

      <ConfirmationModal
        onConfirm={confirmBulkInvoicing}
        isOpen={showBulkInvoicingModal}
        onClose={() => setShowBulkInvoicingModal(false)}
        title={bulkPause ? t.pauseInvoicingTitle : t.resumeInvoicingTitle}
        description={
          bulkPause
            ? `Are you sure you want to pause invoicing for ${selectedIds.size} selected organization${selectedIds.size === 1 ? '' : 's'}? Organizations that have invoicing set up will see a "temporarily unavailable" message and won't be able to send invoices until you resume them.`
            : `Are you sure you want to resume invoicing for ${selectedIds.size} selected organization${selectedIds.size === 1 ? '' : 's'}? They will regain access to the invoicing page.`
        }
        confirmText={bulkPause ? t.pause : t.resume}
        variant="warning"
      />

      <ConfirmationModal
        onConfirm={confirmBulkStatus}
        isOpen={showBulkStatusModal}
        onClose={() => setShowBulkStatusModal(false)}
        title={bulkEnable ? t.enableOrganizationsTitle : t.disableOrganizationsTitle}
        description={
          bulkEnable
            ? `You are about to enable ${selectedIds.size} organization${selectedIds.size === 1 ? '' : 's'}. They will be usable in projects again. Are you sure you want to continue?`
            : `You are about to disable ${selectedIds.size} organization${selectedIds.size === 1 ? '' : 's'}. Disabled organizations can't be used in new projects and their members lose access. This affects everyone in those organizations — are you sure you want to continue?`
        }
        confirmText={bulkEnable ? t.enableAll : t.disableAll}
        variant={bulkEnable ? 'success' : 'danger'}
      />

      <Modal
        isOpen={showUserInfoModal}
        onClose={modalClose}
        title={t.userInformation}
        size="small">
        <UserInfoModal
          modalClose={modalClose}
          userToShow={userToDetail?.email}
          organization={userToDetail?.organization}
        />
      </Modal>

      <CreateEditOrganizationModal
        isOpen={showEditModal}
        onClose={modalClose}
        editing={true}
        editOrganization={handleEditSubmit}
        saving={false}
        initialData={{
          organizationId: organizationToEdit?.organizationId || '',
          name: organizationToEdit?.name || '',
          showAsSubcontractor: organizationToEdit?.showAsSubcontractor || false,
          atavEnabled: organizationToEdit?.atavEnabled || false,
          completenessGateMode: organizationToEdit?.completenessGateMode || CompletenessGateMode.WARN,
        }}
      />
    </div>
  );
};

export default OrganizationsList;
